import React from 'react';
import {Body} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import type {CampaignProps} from '@/types/campaign';
import BriefField from '../shared/BriefField';
import BriefItemHtml from '../shared/BriefItemHtml';
import BriefSection from '../shared/BriefSection';
import LegacyDeliverablesSection from '../shared/LegacyDeliverablesSection';
import TrackingUrlItem from '../shared/TrackingUrlItem';
import decodeHtmlEntities from '../utils/decodeHtmlEntities';
import styles from './BriefContent.module.scss';

interface BriefContentProps {
  campaign: CampaignProps;
}

// Renders the brief the way SWAPS defines it and CWA shows it: every brief item
// verbatim (title + contentHtml) in weight order, with no cherry-picking or
// renaming, plus the scaffold sections sourced from non-brief-item data.
function BriefContent({campaign}: BriefContentProps) {
  const items = [...(campaign.campaignPaidGigBriefItems ?? [])]
    .sort((a, b) => (a.weight ?? 0) - (b.weight ?? 0))
    .filter((item) => !!item.contentHtml || !!item.highlighted);

  const questions = campaign.questions?.filter((q) => q.body) ?? [];
  const trackingUrls = campaign.campaignTrackingUrls ?? [];
  const verticalTags = campaign.verticalTags ?? [];
  const creatorGroups = campaign.creatorGroups ?? [];

  return (
    <div className={styles.container}>
      <LegacyDeliverablesSection campaign={campaign} />
      {campaign.descriptionHtml ? (
        <BriefSection title={translate('Campaign details')}>
          <BriefItemHtml contentHtml={campaign.descriptionHtml} />
        </BriefSection>
      ) : null}
      <div className={styles.sections}>
        {items.map((item, index) => (
          <BriefSection
            key={item.id ?? index}
            title={item.title ? decodeHtmlEntities(item.title) : ''}
          >
            <BriefItemHtml
              contentHtml={item.contentHtml}
              highlighted={item.highlighted}
            />
          </BriefSection>
        ))}
      </div>
      {trackingUrls.length > 0 ? (
        <BriefSection title={translate('Custom URL for posting')}>
          <div className={styles.stack}>
            {trackingUrls.map((trackingUrl) => (
              <TrackingUrlItem
                key={trackingUrl.id ?? trackingUrl.url}
                trackingUrl={trackingUrl}
              />
            ))}
          </div>
        </BriefSection>
      ) : null}
      {questions.length > 0 ? (
        <BriefSection title={translate('Prequalifying questions')}>
          <Body size="md" className={styles.sectionSubtext}>
            {translate(
              'These questions will be answered by creators when applying.'
            )}
          </Body>
          {questions.map((q, index) => (
            <BriefItemHtml key={q.id ?? index} contentHtml={q.body ?? ''} />
          ))}
        </BriefSection>
      ) : null}
      {verticalTags.length > 0 || creatorGroups.length > 0 ? (
        <BriefSection title={translate('Additional campaign details')}>
          <Body size="md" className={styles.sectionSubtext}>
            {translate('These fields are not visible to creators.')}
          </Body>
          {verticalTags.length > 0 ? (
            <BriefField label={translate('Campaign verticals')}>
              <Body size="md">
                {verticalTags.map((t) => t.displayText ?? t.name).join(', ')}
              </Body>
            </BriefField>
          ) : null}
          {creatorGroups.length > 0 ? (
            <BriefField label={translate('Required creator groups and labels')}>
              <Body size="md">
                {creatorGroups.map((g) => g.name).join(', ')}
              </Body>
            </BriefField>
          ) : null}
        </BriefSection>
      ) : null}
    </div>
  );
}

export default BriefContent;
